import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getDatabase, ref, get, query, orderByKey, limitToLast, endBefore, orderByChild, equalTo } from "firebase/database";
import { FaGraduationCap, FaBriefcase, FaChevronLeft, FaChevronRight, FaUnlock, FaBars, FaTimes, FaFilter } from 'react-icons/fa';
import { useAuth } from '../../context/AuthContext';
import UserRegistrationModal from './UserRegistrationModal';
import './UserHomePage.css';

// Global cache to persist data across component unmounts
let globalProfilesCache = [];
let globalCacheTimestamp = null;
let globalLastKey = null; // Track last loaded key for pagination
let globalFilterOptionsCache = null; // Cache for filter dropdown options
let globalFilterCacheTimestamp = null;
const CACHE_DURATION = 30 * 60 * 1000; // 30 minutes — extended to reduce Firebase reads
const BATCH_SIZE = 500; // Load 500 users at a time
const PROFILES_NEAR_END_THRESHOLD = 30; // Load more when user is within 30 profiles (1 page) of the end
const FILTER_CACHE_DAYS = 7; // Cache filter options for 7 days to avoid daily full table scans
const REGISTRATION_CHECK_SESSION_KEY = 'matrimony_reg_checked_'; // sessionStorage key for registration check

const UserHomePage = () => {
    const navigate = useNavigate();
    const { user, logout } = useAuth();
    const [allProfiles, setAllProfiles] = useState([]);
    const [filteredProfiles, setFilteredProfiles] = useState([]);
    const [currentPageProfiles, setCurrentPageProfiles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [showRegistrationModal, setShowRegistrationModal] = useState(false);
    const [isRegistered, setIsRegistered] = useState(false);
    const [checkingRegistration, setCheckingRegistration] = useState(true);
    const [fakeUserCount, setFakeUserCount] = useState(0); // Fake count for display
    const [hasMoreData, setHasMoreData] = useState(true); // Track if more data available
    const [isLoadingMore, setIsLoadingMore] = useState(false); // Track if currently loading next batch
    const [allDataLoaded, setAllDataLoaded] = useState(false); // Track if all data is loaded

    // Filter states
    const [showFilters, setShowFilters] = useState(false);
    const [filters, setFilters] = useState({
        gender: '',
        religion: '',
        caste: '',
        state: '',
        district: '',
        taluka: '',
        education: '',
        maritalStatus: '',
        ageMin: '',
        ageMax: ''
    });
    const [filterOptions, setFilterOptions] = useState({
        religions: [],
        castes: [],
        districts: [],
        talukas: [],
        educations: [],
        maritalStatuses: []
    });


    const profilesPerPage = 30; // 30 profiles per page
    const hasInitialized = useRef(false);
    const isLoadingMoreRef = useRef(false); // Ref to prevent double-loading

    // Helper function to calculate age
    const calculateAge = useCallback((dob) => {
        if (!dob) return null;
        const today = new Date();
        const birthDate = new Date(dob);
        let age = today.getFullYear() - birthDate.getFullYear();
        const m = today.getMonth() - birthDate.getMonth();
        if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
            age--;
        }
        return age;
    }, []);

    // Helper function to check if URL is a valid image
    const isImageUrl = useCallback((url) => {
        if (!url || typeof url !== 'string') return false;

        // Check if it's a Firebase Storage URL (common for these images)
        if (url.includes('firebasestorage.googleapis.com')) return true;

        const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp'];
        const lowerUrl = url.toLowerCase();
        return (lowerUrl.startsWith('http://') || lowerUrl.startsWith('https://')) &&
            (imageExtensions.some(ext => lowerUrl.includes(ext)) || lowerUrl.includes('image'));
    }, []);

    // Check if global cache is valid
    const isCacheValid = useCallback(() => {
        if (!globalProfilesCache || !globalCacheTimestamp) return false;
        const cacheAge = Date.now() - globalCacheTimestamp;
        return cacheAge < CACHE_DURATION;
    }, []);

    // ✅ Extract filter options from already-downloaded profiles — NO extra Firebase read
    // This uses the same 500 profiles fetched for display to build filter dropdowns.
    const extractFilterOptionsFromProfiles = useCallback((profiles) => {
        const religions = new Set();
        const castes = new Set();
        const districts = new Set();
        const talukas = new Set();
        const educations = new Set();
        const maritalStatuses = new Set();

        profiles.forEach((profile) => {
            if (profile.religion && profile.religion !== 'Not specified' && profile.religion.trim())
                religions.add(profile.religion.trim());
            if (profile.caste && profile.caste !== 'Not specified' && profile.caste.trim())
                castes.add(profile.caste.trim());
            if (profile.district && profile.district !== 'Not specified' && profile.district.trim())
                districts.add(profile.district.trim());
            if (profile.taluka && profile.taluka !== 'Not specified' && profile.taluka.trim())
                talukas.add(profile.taluka.trim());
            if (profile.education && profile.education !== 'Not specified' && profile.education.trim())
                educations.add(profile.education.trim());
            if (profile.maritalStatus && profile.maritalStatus !== 'Not specified' && profile.maritalStatus.trim())
                maritalStatuses.add(profile.maritalStatus.trim());
        });

        const options = {
            religions: Array.from(religions).sort(),
            castes: Array.from(castes).sort(),
            districts: Array.from(districts).sort(),
            talukas: Array.from(talukas).sort(),
            educations: Array.from(educations).sort(),
            maritalStatuses: Array.from(maritalStatuses).sort()
        };

        // Save to cache
        globalFilterOptionsCache = options;
        globalFilterCacheTimestamp = Date.now();
        localStorage.setItem('matrimony_filter_options', JSON.stringify(options));
        localStorage.setItem('matrimony_filter_cache_date', globalFilterCacheTimestamp.toString());
        localStorage.setItem('matrimony_filter_cache_time', globalFilterCacheTimestamp.toString());

        setFilterOptions(options);
        console.log('Filter options extracted from loaded profiles (0 extra Firebase reads):', {
            religions: options.religions.length,
            castes: options.castes.length,
            districts: options.districts.length
        });
        return options;
    }, []);



    // Apply filters to profiles - ALL filters use case-insensitive matching
    const applyFilters = useCallback((profiles, filterCriteria) => {
        if (!profiles || !Array.isArray(profiles)) return [];

        return profiles.filter(profile => {
            if (!profile) return false;

            // Gender filter - case-insensitive
            if (filterCriteria.gender) {
                const profileGender = (profile.gender || '').toLowerCase().trim();
                const filterGender = filterCriteria.gender.toLowerCase().trim();
                if (profileGender !== filterGender) return false;
            }

            // Religion filter - case-insensitive
            if (filterCriteria.religion) {
                const profileReligion = (profile.religion || '').toLowerCase().trim();
                const filterReligion = filterCriteria.religion.toLowerCase().trim();
                if (profileReligion !== filterReligion) return false;
            }

            // Caste filter - case-insensitive partial matching
            if (filterCriteria.caste) {
                const filterCaste = filterCriteria.caste.toLowerCase().trim();
                const profileCaste = (profile.caste || '').toLowerCase().trim();
                if (!profileCaste.includes(filterCaste) && !filterCaste.includes(profileCaste)) {
                    return false;
                }
            }

            // State filter - case-insensitive
            if (filterCriteria.state) {
                const profileState = (profile.state || 'Maharashtra').toLowerCase().trim();
                const filterState = filterCriteria.state.toLowerCase().trim();
                if (profileState !== filterState) return false;
            }

            // District filter - case-insensitive
            if (filterCriteria.district) {
                const filterDistrict = filterCriteria.district.toLowerCase().trim();
                const profileDistrict = (profile.district || '').toLowerCase().trim();
                const profileLocation = (profile.location || '').toLowerCase().trim();
                const profileCurrentPlace = (profile.currentPlace || '').toLowerCase().trim();
                const profileNativePlace = (profile.nativePlace || '').toLowerCase().trim();

                const hasDistrict = profileDistrict.includes(filterDistrict) ||
                    filterDistrict.includes(profileDistrict) ||
                    profileLocation.includes(filterDistrict) ||
                    profileCurrentPlace.includes(filterDistrict) ||
                    profileNativePlace.includes(filterDistrict);
                if (!hasDistrict) return false;
            }

            // Taluka filter - case-insensitive
            if (filterCriteria.taluka) {
                const filterTaluka = filterCriteria.taluka.toLowerCase().trim();
                const profileTaluka = (profile.taluka || '').toLowerCase().trim();
                const profileLocation = (profile.location || '').toLowerCase().trim();
                const profileNativePlace = (profile.nativePlace || '').toLowerCase().trim();

                const hasTaluka = profileTaluka.includes(filterTaluka) ||
                    filterTaluka.includes(profileTaluka) ||
                    profileLocation.includes(filterTaluka) ||
                    profileNativePlace.includes(filterTaluka);
                if (!hasTaluka) return false;
            }

            // Education filter - case-insensitive partial match
            if (filterCriteria.education) {
                const profileEducation = (profile.education || '').toLowerCase();
                const filterEducation = filterCriteria.education.toLowerCase();
                if (!profileEducation.includes(filterEducation)) return false;
            }

            // Marital Status filter - case-insensitive
            if (filterCriteria.maritalStatus) {
                const profileStatus = (profile.maritalStatus || '').toLowerCase().trim();
                const filterStatus = filterCriteria.maritalStatus.toLowerCase().trim();
                if (profileStatus !== filterStatus) return false;
            }

            // Age filter
            if (filterCriteria.ageMin || filterCriteria.ageMax) {
                const age = typeof profile.age === 'number' ? profile.age : parseInt(profile.age);
                if (isNaN(age)) return false;

                if (filterCriteria.ageMin && age < parseInt(filterCriteria.ageMin)) {
                    return false;
                }
                if (filterCriteria.ageMax && age > parseInt(filterCriteria.ageMax)) {
                    return false;
                }
            }

            return true;
        });
    }, []);

    // Handle filter changes
    const handleFilterChange = useCallback((filterName, value) => {
        setFilters(prev => ({
            ...prev,
            [filterName]: value
        }));
    }, []);

    // Process raw Firebase user entries into profile objects
    const processUserEntries = useCallback((userEntries) => {
        return userEntries.map(([key, value]) => {
            const personal = value.personal || {};
            const educational = value.educational || {};

            const fullName = [personal.firstName, personal.middleName, personal.lastName]
                .filter(Boolean)
                .join(" ");

            const age = personal.dateOfBirth ? calculateAge(personal.dateOfBirth) : null;

            let photo = null;
            if (value.photos && Array.isArray(value.photos) && value.photos.length > 0) {
                const validPhotos = value.photos.filter(photoUrl =>
                    photoUrl && typeof photoUrl === 'string' && isImageUrl(photoUrl)
                );
                if (validPhotos.length > 0) {
                    photo = validPhotos[0];
                }
            }

            return {
                id: key,
                name: fullName || "Name not provided",
                age: age || "Not specified",
                gender: personal.gender || "Not specified",
                location: educational.currentPlace || educational.district || educational.nativePlace || "Not specified",
                profession: educational.profession || "Not specified",
                education: educational.education || "Not specified",
                photo: photo,
                phoneNumber: personal.phoneNumber || key,
                religion: personal.religion || "Not specified",
                caste: personal.caste || "Not specified",
                maritalStatus: personal.maritalStatus || "Not specified",
                height: personal.heightFeet && personal.heightInches ?
                    `${personal.heightFeet}'${personal.heightInches}"` : "Not specified",
                state: (value.state || (educational && educational.state) || "Maharashtra"),
                district: (educational && educational.district) || "Not specified",
                taluka: (educational && educational.taluka) || "Not specified",
                currentPlace: (educational && educational.currentPlace) || "Not specified",
                nativePlace: (educational && educational.nativePlace) || "Not specified"
            };
        }).reverse();
    }, [calculateAge, isImageUrl]);

    // Load next batch of 500 profiles ON DEMAND - appends silently without disturbing current view
    const loadNextBatch = useCallback(async () => {
        if (isLoadingMoreRef.current || !hasMoreData || allDataLoaded) return;

        isLoadingMoreRef.current = true;
        setIsLoadingMore(true);
        console.log('Loading next batch of profiles...');

        try {
            const db = getDatabase();

            if (!globalLastKey) {
                console.log('No last key available, cannot load more');
                setHasMoreData(false);
                setAllDataLoaded(true);
                return;
            }

            const usersQuery = query(
                ref(db, "Matrimony/users"),
                orderByKey(),
                endBefore(globalLastKey),
                limitToLast(BATCH_SIZE)
            );

            const snapshot = await get(usersQuery);

            if (snapshot.exists()) {
                const usersData = snapshot.val();
                const userEntries = Object.entries(usersData);

                if (userEntries.length === 0) {
                    setHasMoreData(false);
                    setAllDataLoaded(true);
                    console.log('No more profiles to load');
                    return;
                }

                // Update the last key for the NEXT batch
                globalLastKey = userEntries[0][0];
                const processedUsers = processUserEntries(userEntries);

                // APPEND new profiles to existing cache - don't disturb existing order
                const combinedProfiles = [...globalProfilesCache, ...processedUsers];
                globalProfilesCache = combinedProfiles;
                globalCacheTimestamp = Date.now();

                // Check if there's more data
                const hasMore = userEntries.length >= BATCH_SIZE * 0.5;

                // SILENTLY update state - no page jump, no re-render of current view
                setAllProfiles(combinedProfiles);
                setHasMoreData(hasMore);
                if (!hasMore) setAllDataLoaded(true);

                // Update total pages so user can navigate forward
                const hasActiveFilters = Object.values(filters).some(value => value !== '');
                if (hasActiveFilters) {
                    const filtered = applyFilters(combinedProfiles, filters);
                    setFilteredProfiles(filtered);
                    setTotalPages(Math.ceil(filtered.length / profilesPerPage));
                } else {
                    setFilteredProfiles(combinedProfiles);
                    setTotalPages(Math.ceil(combinedProfiles.length / profilesPerPage));
                }

                console.log(`Loaded ${userEntries.length} more profiles. Total: ${combinedProfiles.length}. Has more: ${hasMore}`);
            } else {
                setHasMoreData(false);
                setAllDataLoaded(true);
                console.log('No more profiles available in database');
            }
        } catch (error) {
            console.error('Error loading next batch:', error);
        } finally {
            isLoadingMoreRef.current = false;
            setIsLoadingMore(false);
        }
    }, [hasMoreData, allDataLoaded, processUserEntries, filters, applyFilters, profilesPerPage]);

    // Clear all filters
    const clearFilters = useCallback(() => {
        setFilters({
            gender: '',
            religion: '',
            caste: '',
            state: '',
            district: '',
            taluka: '',
            education: '',
            maritalStatus: '',
            ageMin: '',
            ageMax: ''
        });
    }, []);

    // Apply filters INSTANTLY on currently loaded data - auto-loads more if 0 results with multiple filters
    const applyFiltersSmartly = useCallback(async () => {
        const hasActiveFilters = Object.values(filters).some(value => value !== '');

        if (!hasActiveFilters) {
            // No filters - show all loaded profiles
            setFilteredProfiles(allProfiles);
            setTotalPages(Math.ceil(allProfiles.length / profilesPerPage));
            setCurrentPage(0);
            setCurrentPageProfiles(allProfiles.slice(0, profilesPerPage));
            return;
        }

        // Apply filters INSTANTLY to whatever data we have loaded
        let filtered = applyFilters(allProfiles, filters);

        // SMART AUTO-LOADING: If multiple filters produce 0 results but more data exists,
        // automatically load more data to find matching profiles
        // ✅ FIX: Capped at 1 attempt to prevent cascading Firebase reads that inflate the bill
        let loadAttempts = 0;
        const MAX_AUTO_LOAD_ATTEMPTS = 1;

        // SPECIAL CASE: If State filter (e.g. Bihar) is active and we have 0 matches, 
        // use INDEXED QUERY to fetch from that state immediately
        if (filters.state && filtered.length === 0 && hasMoreData && !allDataLoaded) {
            console.log(`Using indexed query for state: ${filters.state}`);
            setIsLoadingMore(true);
            try {
                const db = getDatabase();

                // Fetch up to 1000 users from this state directly using indexing
                // We check root 'state' which was added specifically for indexing
                const stateQuery = query(
                    ref(db, "Matrimony/users"),
                    orderByChild('state'),
                    equalTo(filters.state),
                    limitToLast(1000)
                );

                const snapshot = await get(stateQuery);
                if (snapshot.exists()) {
                    const usersData = snapshot.val();
                    const userEntries = Object.entries(usersData);
                    const processedUsers = processUserEntries(userEntries);

                    // Merge with cache but avoid duplicates
                    const existingIds = new Set(globalProfilesCache.map(p => p.id));
                    const uniqueNewUsers = processedUsers.filter(p => !existingIds.has(p.id));

                    const combinedProfiles = [...globalProfilesCache, ...uniqueNewUsers];
                    globalProfilesCache = combinedProfiles;
                    globalCacheTimestamp = Date.now();

                    setAllProfiles(combinedProfiles);
                    filtered = applyFilters(combinedProfiles, filters);
                    console.log(`Indexed query found ${userEntries.length} profiles from ${filters.state}.`);
                }
            } catch (error) {
                console.error('Error during indexed state query:', error);
            } finally {
                setIsLoadingMore(false);
            }
        }

        // Fallback to standard batch loading if indexed query didn't find enough or wasn't applicable
        while (filtered.length === 0 && hasMoreData && !allDataLoaded && !isLoadingMoreRef.current && loadAttempts < MAX_AUTO_LOAD_ATTEMPTS) {
            loadAttempts++;
            // ... (rest of the standard loading logic remains as a fallback)
            console.log(`No results found yet. Standard loading attempt ${loadAttempts}...`);
            isLoadingMoreRef.current = true;
            setIsLoadingMore(true);

            try {
                const db = getDatabase();
                if (!globalLastKey) break;

                const usersQuery = query(
                    ref(db, "Matrimony/users"),
                    orderByKey(),
                    endBefore(globalLastKey),
                    limitToLast(BATCH_SIZE)
                );

                const snapshot = await get(usersQuery);

                if (snapshot.exists()) {
                    const usersData = snapshot.val();
                    const userEntries = Object.entries(usersData);

                    if (userEntries.length === 0) {
                        setHasMoreData(false);
                        setAllDataLoaded(true);
                        break;
                    }

                    globalLastKey = userEntries[0][0];
                    const processedUsers = processUserEntries(userEntries);
                    const existingIds = new Set(globalProfilesCache.map(p => p.id));
                    const uniqueNewUsers = processedUsers.filter(p => !existingIds.has(p.id));

                    const combinedProfiles = [...globalProfilesCache, ...uniqueNewUsers];
                    globalProfilesCache = combinedProfiles;
                    globalCacheTimestamp = Date.now();

                    const hasMore = userEntries.length >= BATCH_SIZE * 0.5;
                    setAllProfiles(combinedProfiles);
                    setHasMoreData(hasMore);
                    if (!hasMore) setAllDataLoaded(true);

                    filtered = applyFilters(combinedProfiles, filters);
                } else {
                    setHasMoreData(false);
                    setAllDataLoaded(true);
                    break;
                }
            } catch (error) {
                console.error('Error auto-loading for filters:', error);
                break;
            } finally {
                isLoadingMoreRef.current = false;
                setIsLoadingMore(false);
            }
        }

        setFilteredProfiles(filtered);
        setTotalPages(Math.ceil(filtered.length / profilesPerPage));
        setCurrentPage(0);
        setCurrentPageProfiles(filtered.slice(0, profilesPerPage));
    }, [filters, allProfiles, applyFilters, profilesPerPage, hasMoreData, allDataLoaded, processUserEntries]);

    // Check if user is registered
    const checkUserRegistration = useCallback(async () => {
        setCheckingRegistration(true);

        // Check localStorage for the key that AuthContext uses
        const savedUser = localStorage.getItem('matrimony_user');
        if (savedUser) {
            const userData = JSON.parse(savedUser);

            // Extract phone number from the saved user data
            let phoneNumber = userData.phoneNumber;
            if (phoneNumber.startsWith('+91')) {
                phoneNumber = phoneNumber.substring(3);
            } else if (phoneNumber.startsWith('91') && phoneNumber.length === 12) {
                phoneNumber = phoneNumber.substring(2);
            }

            // ✅ FIX: Check sessionStorage first to avoid Firebase read on every navigation
            // This survives tab navigation within the same session without re-querying Firebase
            const sessionKey = REGISTRATION_CHECK_SESSION_KEY + phoneNumber;
            const sessionChecked = sessionStorage.getItem(sessionKey);
            if (sessionChecked === 'true') {
                setIsRegistered(true);
                setCheckingRegistration(false);
                return true;
            }

            // Check if user has complete profile in Firebase (only once per session)
            try {
                const db = getDatabase();
                const userRef = ref(db, `Matrimony/users/${phoneNumber}`);
                const snapshot = await get(userRef);

                if (snapshot.exists()) {
                    const profileData = snapshot.val();

                    // Check if profile is complete
                    const hasCompleteProfile = (
                        profileData.personal?.firstName &&
                        profileData.personal?.lastName &&
                        profileData.personal?.dateOfBirth &&
                        profileData.personal?.heightFeet &&
                        profileData.personal?.heightInches &&
                        profileData.personal?.maritalStatus &&
                        profileData.personal?.caste &&
                        profileData.personal?.religion &&
                        profileData.educational?.education &&
                        profileData.educational?.profession &&
                        profileData.educational?.currentPlace &&
                        profileData.educational?.nativePlace &&
                        profileData.educational?.taluka &&
                        profileData.educational?.district &&
                        profileData.photos &&
                        profileData.photos.length > 0 &&
                        profileData.biodata
                    );

                    if (hasCompleteProfile) {
                        // ✅ Cache result in sessionStorage — no Firebase read on next navigation
                        sessionStorage.setItem(sessionKey, 'true');
                        setIsRegistered(true);
                        setCheckingRegistration(false);
                        return true;
                    }
                }
            } catch (error) {
                console.error('Error checking profile:', error);
            }
        }

        // If no localStorage or incomplete profile, show registration modal
        setIsRegistered(false);
        setShowRegistrationModal(true);
        setCheckingRegistration(false);
        return false;
    }, []);

    // Handle registration completion
    const handleRegistrationComplete = (userData) => {
        localStorage.setItem('matrimony_user', JSON.stringify({
            phoneNumber: `+91${userData.phoneNumber}`,
            id: `user_${userData.phoneNumber}`
        }));
        setIsRegistered(true);
        setShowRegistrationModal(false);

        // Reload the page to fetch profiles
        window.location.reload();
    };

    // Fetch initial batch of users from Firebase - NO auto background loading
    const fetchUsers = useCallback(async () => {
        try {
            setLoading(true);
            const db = getDatabase();

            // Initial load - get latest 500 users
            const usersQuery = query(
                ref(db, "Matrimony/users"),
                orderByKey(),
                limitToLast(BATCH_SIZE)
            );

            const snapshot = await get(usersQuery);

            if (snapshot.exists()) {
                const usersData = snapshot.val();
                const userEntries = Object.entries(usersData);

                // Store the first key for next batch loading
                if (userEntries.length > 0) {
                    globalLastKey = userEntries[0][0];
                }

                const processedUsers = processUserEntries(userEntries);

                // Update global cache
                globalProfilesCache = processedUsers;
                globalCacheTimestamp = Date.now();

                const hasMore = userEntries.length >= BATCH_SIZE * 0.5;
                console.log('Initial load:', userEntries.length, 'profiles. Has more:', hasMore);
                setHasMoreData(hasMore);

                // Generate fake count
                const fakeCount = Math.floor(Math.random() * (117000 - 112000 + 1)) + 112000;
                setFakeUserCount(fakeCount);

                setAllProfiles(processedUsers);
                setFilteredProfiles(processedUsers);
                setTotalPages(Math.ceil(processedUsers.length / profilesPerPage));

                // ✅ Extract filter options from the already-downloaded profiles — ZERO extra Firebase read
                // This replaces the old loadFilterOptions() full-table scan entirely.
                const cachedTimestamp = parseInt(localStorage.getItem('matrimony_filter_cache_date') || '0');
                const cacheAgeMs = Date.now() - cachedTimestamp;
                const sevenDaysMs = FILTER_CACHE_DAYS * 24 * 60 * 60 * 1000;

                if (cacheAgeMs >= sevenDaysMs || !globalFilterOptionsCache) {
                    // Build filter options from the profiles we already downloaded (no new Firebase read)
                    extractFilterOptionsFromProfiles(processedUsers);
                } else {
                    // Use cached options from localStorage
                    const cachedOptions = localStorage.getItem('matrimony_filter_options');
                    const cachedTime = localStorage.getItem('matrimony_filter_cache_time');
                    if (cachedOptions && cachedTime) {
                        try {
                            const options = JSON.parse(cachedOptions);
                            globalFilterOptionsCache = options;
                            globalFilterCacheTimestamp = parseInt(cachedTime);
                            setFilterOptions(options);
                        } catch (error) {
                            console.error('Error parsing cached options:', error);
                            extractFilterOptionsFromProfiles(processedUsers);
                        }
                    } else {
                        extractFilterOptionsFromProfiles(processedUsers);
                    }
                }

                // NO auto background loading - data loads only when user navigates near end
                return processedUsers;
            } else {
                setAllProfiles([]);
                setFilteredProfiles([]);
                setCurrentPageProfiles([]);
                setTotalPages(0);
                return [];
            }
        } catch (error) {
            console.error("Error fetching users:", error);
            setAllProfiles([]);
            setFilteredProfiles([]);
            setCurrentPageProfiles([]);
            setTotalPages(0);
            return [];
        } finally {
            setLoading(false);
        }
    }, [processUserEntries, profilesPerPage, extractFilterOptionsFromProfiles]);

    // Handle profile click
    const handleProfileClick = (profile) => {
        // Save current state
        const currentState = {
            currentPage,
            scrollPosition: window.scrollY
        };

        sessionStorage.setItem('userHomePageState', JSON.stringify(currentState));
        sessionStorage.setItem('returningFromDetail', 'true');

        const isAdmin = user && (user.phoneNumber === "9370329233" || user.phoneNumber === "+919370329233");
        if (isAdmin) {
            navigate(`/admin/user-details/${profile.id}`);
        } else {
            navigate(`/user/${profile.id}`);
        }
    };

    // Handle pagination - instant page change, smart on-demand loading
    const handlePageChange = useCallback(async (pageNumber) => {
        const hasActiveFilters = Object.values(filters).some(value => value !== '');
        let profilesToUse = hasActiveFilters ? filteredProfiles : allProfiles;

        // Calculate which profile index the user will be viewing
        const startIndex = pageNumber * profilesPerPage;
        const endIndex = startIndex + profilesPerPage;

        // Check if we're trying to go beyond currently loaded data
        const pageProfiles = profilesToUse.slice(startIndex, endIndex);

        if (pageProfiles.length === 0 && hasMoreData && !allDataLoaded && !isLoadingMoreRef.current) {
            // User reached the end of loaded data - load more before showing empty page
            console.log(`Last page reached, loading next ${BATCH_SIZE} profiles...`);
            setIsLoadingMore(true);
            await loadNextBatch();
            setIsLoadingMore(false);

            // After loading, recalculate with fresh data
            const updatedProfiles = hasActiveFilters
                ? applyFilters(globalProfilesCache, filters)
                : globalProfilesCache;

            const newPageProfiles = updatedProfiles.slice(startIndex, endIndex);

            if (newPageProfiles.length > 0) {
                setCurrentPage(pageNumber);
                setCurrentPageProfiles(newPageProfiles);
                window.scrollTo({ top: 0, behavior: 'smooth' });
            } else {
                // Still no data after loading - stay on current page
                console.log('No more matching profiles found after loading');
            }
            return;
        }

        // INSTANTLY show the page - no loading, no waiting
        setCurrentPage(pageNumber);
        setCurrentPageProfiles(pageProfiles);
        window.scrollTo({ top: 0, behavior: 'smooth' });

        // SMART LOADING: Check if user is near the end of loaded data
        // Example: 500 profiles loaded, user reaches page with profile ~490 → load next 500
        const lastProfileIndexOnThisPage = endIndex;
        const totalLoadedProfiles = allProfiles.length;
        const profilesRemaining = totalLoadedProfiles - lastProfileIndexOnThisPage;

        if (profilesRemaining <= PROFILES_NEAR_END_THRESHOLD && hasMoreData && !allDataLoaded && !isLoadingMoreRef.current) {
            console.log(`User near end (profile ${lastProfileIndexOnThisPage}/${totalLoadedProfiles}, ${profilesRemaining} remaining). Loading next ${BATCH_SIZE}...`);
            await loadNextBatch();
        }
    }, [filters, filteredProfiles, allProfiles, profilesPerPage, hasMoreData, allDataLoaded, loadNextBatch, applyFilters]);

    // Initialize component
    useEffect(() => {
        if (hasInitialized.current) return;
        hasInitialized.current = true;

        // Check registration first
        checkUserRegistration().then((registered) => {
            if (!registered) {
                // User not registered, modal will show
                setLoading(false);
                return;
            }

            // User is registered, proceed with loading profiles
            const returningFromDetail = sessionStorage.getItem('returningFromDetail');

            if (returningFromDetail === 'true') {
                // Returning from detail page
                if (isCacheValid()) {
                    // Use cached data instantly - no loading screen
                    setAllProfiles(globalProfilesCache);
                    setTotalPages(Math.ceil(globalProfilesCache.length / profilesPerPage));

                    // Restore state
                    const savedState = sessionStorage.getItem('userHomePageState');
                    if (savedState) {
                        try {
                            const state = JSON.parse(savedState);
                            const pageToShow = state.currentPage || 0;

                            setCurrentPage(pageToShow);
                            const startIndex = pageToShow * profilesPerPage;
                            const endIndex = startIndex + profilesPerPage;
                            setCurrentPageProfiles(globalProfilesCache.slice(startIndex, endIndex));

                            // Restore scroll position instantly
                            setTimeout(() => {
                                window.scrollTo({
                                    top: state.scrollPosition || 0,
                                    behavior: 'instant'
                                });
                            }, 0);
                        } catch (error) {
                            console.error('Error restoring state:', error);
                            setCurrentPage(0);
                            setCurrentPageProfiles(globalProfilesCache.slice(0, profilesPerPage));
                        }
                    }

                    setLoading(false);

                    // Clear flags
                    sessionStorage.removeItem('returningFromDetail');
                    sessionStorage.removeItem('userHomePageState');
                } else {
                    // Cache expired - fetch fresh data
                    fetchUsers().then((profiles) => {
                        if (profiles && profiles.length > 0) {
                            const savedState = sessionStorage.getItem('userHomePageState');
                            if (savedState) {
                                try {
                                    const state = JSON.parse(savedState);
                                    const pageToShow = state.currentPage || 0;

                                    setCurrentPage(pageToShow);
                                    const startIndex = pageToShow * profilesPerPage;
                                    const endIndex = startIndex + profilesPerPage;
                                    setCurrentPageProfiles(profiles.slice(startIndex, endIndex));

                                    setTimeout(() => {
                                        window.scrollTo({
                                            top: state.scrollPosition || 0,
                                            behavior: 'instant'
                                        });
                                    }, 100);
                                } catch (error) {
                                    setCurrentPage(0);
                                    setCurrentPageProfiles(profiles.slice(0, profilesPerPage));
                                }
                            }
                        }

                        sessionStorage.removeItem('returningFromDetail');
                        sessionStorage.removeItem('userHomePageState');
                    });
                }
            } else {
                // Fresh load
                if (isCacheValid()) {
                    // Use cached data
                    setAllProfiles(globalProfilesCache);
                    setTotalPages(Math.ceil(globalProfilesCache.length / profilesPerPage));
                    setCurrentPage(0);
                    setCurrentPageProfiles(globalProfilesCache.slice(0, profilesPerPage));
                    setLoading(false);
                } else {
                    // Fetch fresh data
                    fetchUsers().then((profiles) => {
                        if (profiles && profiles.length > 0) {
                            setCurrentPage(0);
                            setCurrentPageProfiles(profiles.slice(0, profilesPerPage));
                        }
                    });
                }
            }
        });
    }, [isCacheValid, fetchUsers, profilesPerPage, checkUserRegistration]);

    // Apply filters when filter selection changes - INSTANT, no data loading
    useEffect(() => {
        if (allProfiles.length > 0) {
            applyFiltersSmartly();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters]);

    // Load filter options from localStorage on component mount
    useEffect(() => {
        const cachedTimestamp = parseInt(localStorage.getItem('matrimony_filter_cache_date') || '0');
        const cacheAgeMs = Date.now() - cachedTimestamp;
        const sevenDaysMs = FILTER_CACHE_DAYS * 24 * 60 * 60 * 1000;
        const cachedOptions = localStorage.getItem('matrimony_filter_options');
        const cachedTime = localStorage.getItem('matrimony_filter_cache_time');

        // Check if we have valid 7-day cache
        if (cacheAgeMs < sevenDaysMs && cachedOptions && cachedTime) {
            try {
                const options = JSON.parse(cachedOptions);
                globalFilterOptionsCache = options;
                globalFilterCacheTimestamp = parseInt(cachedTime);
                setFilterOptions(options);
                console.log('Loaded filter options from 7-day cache');
            } catch (error) {
                console.error('Error parsing cached filter options:', error);
                // If parsing fails, clear the cache and load fresh data
                localStorage.removeItem('matrimony_filter_options');
                localStorage.removeItem('matrimony_filter_cache_date');
                localStorage.removeItem('matrimony_filter_cache_time');
            }
        } else {
            console.log('Filter cache expired or not found, will load fresh data');
            // Cache is expired or doesn't exist, will be loaded when profiles are fetched
        }
    }, []);

    return (
        <div style={{
            fontFamily: 'Arial, sans-serif',
            background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
            minHeight: '100vh',
            width: '100%',
            boxSizing: 'border-box'
        }}>
            {/* Registration Modal */}
            <UserRegistrationModal
                isOpen={showRegistrationModal}
                onClose={() => {
                    // Modal will close automatically after successful registration
                    // This handler is for when user tries to close without completing
                    if (!isRegistered) {
                        alert('Please complete registration to browse profiles');
                    }
                }}
                onRegistrationComplete={handleRegistrationComplete}
            />

            {/* Show content only if registered */}
            {!checkingRegistration && isRegistered && (
                <>
                    {/* Navigation Header */}
                    <header style={{
                        background: 'rgba(255, 255, 255, 0.95)',
                        backdropFilter: 'blur(10px)',
                        color: '#1e293b',
                        padding: '0.75rem 0',
                        position: 'sticky',
                        top: 0,
                        width: '100%',
                        zIndex: 1000,
                        boxShadow: '0 2px 20px rgba(0,0,0,0.1)',
                        marginBottom: '2rem'
                    }}>
                        <div style={{
                            maxWidth: '1400px',
                            margin: '0 auto',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: '0 1rem'
                        }}>
                            {/* Logo and Website Name */}
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: window.innerWidth > 768 ? '1rem' : '0.5rem',
                                flex: 1
                            }}>
                                <img
                                    src={require('../../assets/img1.jpeg')}
                                    alt="Swrajya Sangam Logo"
                                    style={{
                                        width: window.innerWidth > 768 ? '50px' : '40px',
                                        height: window.innerWidth > 768 ? '50px' : '40px',
                                        borderRadius: window.innerWidth > 768 ? '10px' : '8px',
                                        objectFit: 'cover',
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                                        flexShrink: 0
                                    }}
                                />
                                <div style={{ minWidth: 0 }}>
                                    <h1 style={{
                                        margin: 0,
                                        fontSize: window.innerWidth > 768 ? '1.8rem' : '1.1rem',
                                        fontWeight: 'bold',
                                        background: 'linear-gradient(135deg, #6a11cb 0%, #2575fc 100%)',
                                        WebkitBackgroundClip: 'text',
                                        WebkitTextFillColor: 'transparent',
                                        lineHeight: 1.2,
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis'
                                    }}>
                                        Swrajya Sangam
                                    </h1>
                                    {window.innerWidth > 768 && (
                                        <p style={{
                                            margin: 0,
                                            fontSize: '0.85rem',
                                            color: '#64748b',
                                            fontWeight: '500'
                                        }}>
                                            Find Your Perfect Match
                                        </p>
                                    )}
                                </div>
                            </div>

                            {/* Desktop Navigation */}
                            <nav style={{
                                display: window.innerWidth > 768 ? 'flex' : 'none',
                                gap: '1rem',
                                alignItems: 'center'
                            }}>
                                {user && (
                                    <button
                                        onClick={() => navigate('/unlocked-profiles')}
                                        style={{
                                            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                                            color: 'white',
                                            border: 'none',
                                            padding: '0.75rem 1.5rem',
                                            borderRadius: '12px',
                                            fontWeight: 'bold',
                                            fontSize: '0.95rem',
                                            cursor: 'pointer',
                                            transition: 'all 0.3s ease',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '0.5rem',
                                            boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)',
                                            whiteSpace: 'nowrap'
                                        }}
                                        onMouseOver={(e) => {
                                            e.target.style.transform = 'translateY(-2px)';
                                            e.target.style.boxShadow = '0 6px 16px rgba(16, 185, 129, 0.4)';
                                        }}
                                        onMouseOut={(e) => {
                                            e.target.style.transform = 'translateY(0)';
                                            e.target.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.3)';
                                        }}
                                    >
                                        <FaUnlock />
                                        My Unlocked
                                    </button>
                                )}
                                {!user ? (
                                    <button
                                        onClick={() => navigate('/login')}
                                        style={{
                                            background: 'linear-gradient(135deg, #6a11cb 0%, #2575fc 100%)',
                                            color: 'white',
                                            border: 'none',
                                            padding: '0.75rem 1.5rem',
                                            borderRadius: '12px',
                                            fontWeight: 'bold',
                                            fontSize: '0.95rem',
                                            cursor: 'pointer',
                                            transition: 'all 0.3s ease',
                                            boxShadow: '0 4px 12px rgba(106, 17, 203, 0.3)',
                                            whiteSpace: 'nowrap'
                                        }}
                                        onMouseOver={(e) => {
                                            e.target.style.transform = 'translateY(-2px)';
                                            e.target.style.boxShadow = '0 6px 16px rgba(106, 17, 203, 0.4)';
                                        }}
                                        onMouseOut={(e) => {
                                            e.target.style.transform = 'translateY(0)';
                                            e.target.style.boxShadow = '0 4px 12px rgba(106, 17, 203, 0.3)';
                                        }}
                                    >
                                        Admin Login
                                    </button>
                                ) : (
                                    <>
                                        <div style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '0.5rem',
                                            background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
                                            padding: '0.75rem 1.25rem',
                                            borderRadius: '12px',
                                            border: '2px solid #0ea5e9'
                                        }}>
                                            <span style={{ fontSize: '1.2rem' }}>📱</span>
                                            <span style={{
                                                color: '#0c4a6e',
                                                fontSize: '0.95rem',
                                                fontWeight: '600'
                                            }}>
                                                {user.phoneNumber}
                                            </span>
                                        </div>
                                        <button
                                            onClick={() => {
                                                if (window.confirm('Are you sure you want to logout?')) {
                                                    logout();
                                                    alert('Logged out successfully!');
                                                }
                                            }}
                                            style={{
                                                background: '#ef4444',
                                                color: 'white',
                                                border: 'none',
                                                padding: '0.75rem 1.5rem',
                                                borderRadius: '12px',
                                                fontWeight: 'bold',
                                                fontSize: '0.95rem',
                                                cursor: 'pointer',
                                                transition: 'all 0.3s ease',
                                                boxShadow: '0 4px 12px rgba(239, 68, 68, 0.3)',
                                                whiteSpace: 'nowrap'
                                            }}
                                            onMouseOver={(e) => {
                                                e.target.style.transform = 'translateY(-2px)';
                                                e.target.style.boxShadow = '0 6px 16px rgba(239, 68, 68, 0.4)';
                                            }}
                                            onMouseOut={(e) => {
                                                e.target.style.transform = 'translateY(0)';
                                                e.target.style.boxShadow = '0 4px 12px rgba(239, 68, 68, 0.3)';
                                            }}
                                        >
                                            Logout
                                        </button>
                                    </>
                                )}
                            </nav>

                            {/* Mobile: My Unlocked Button + Menu */}
                            <div style={{
                                display: window.innerWidth <= 768 ? 'flex' : 'none',
                                gap: '0.5rem',
                                alignItems: 'center'
                            }}>
                                {user && (
                                    <button
                                        onClick={() => navigate('/unlocked-profiles')}
                                        style={{
                                            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                                            color: 'white',
                                            border: 'none',
                                            padding: '0.6rem 1rem',
                                            borderRadius: '10px',
                                            fontWeight: 'bold',
                                            fontSize: '0.8rem',
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '0.4rem',
                                            boxShadow: '0 2px 8px rgba(16, 185, 129, 0.3)',
                                            whiteSpace: 'nowrap'
                                        }}
                                    >
                                        <FaUnlock style={{ fontSize: '0.9rem' }} />
                                        My Unlocked
                                    </button>
                                )}
                                <button
                                    onClick={() => setIsMenuOpen(!isMenuOpen)}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        background: 'linear-gradient(135deg, #6a11cb 0%, #2575fc 100%)',
                                        color: 'white',
                                        border: 'none',
                                        fontSize: '1.2rem',
                                        cursor: 'pointer',
                                        padding: '0.6rem',
                                        borderRadius: '8px',
                                        boxShadow: '0 2px 8px rgba(106, 17, 203, 0.3)',
                                        minWidth: '40px',
                                        minHeight: '40px'
                                    }}
                                >
                                    {isMenuOpen ? <FaTimes /> : <FaBars />}
                                </button>
                            </div>
                        </div>

                        {/* Mobile Navigation Menu */}
                        {isMenuOpen && (
                            <div style={{
                                position: 'absolute',
                                top: '100%',
                                left: 0,
                                right: 0,
                                background: 'white',
                                boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                                padding: '1rem',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '0.75rem',
                                animation: 'slideDown 0.3s ease'
                            }}>
                                {!user ? (
                                    <button
                                        onClick={() => {
                                            navigate('/login');
                                            setIsMenuOpen(false);
                                        }}
                                        style={{
                                            background: 'linear-gradient(135deg, #6a11cb 0%, #2575fc 100%)',
                                            color: 'white',
                                            border: 'none',
                                            padding: '0.9rem',
                                            borderRadius: '10px',
                                            fontWeight: 'bold',
                                            fontSize: '0.95rem',
                                            cursor: 'pointer',
                                            boxShadow: '0 2px 8px rgba(106, 17, 203, 0.3)'
                                        }}
                                    >
                                        Admin Login
                                    </button>
                                ) : (
                                    <button
                                        onClick={() => {
                                            if (window.confirm('Are you sure you want to logout?')) {
                                                logout();
                                                setIsMenuOpen(false);
                                                alert('Logged out successfully!');
                                            }
                                        }}
                                        style={{
                                            background: '#ef4444',
                                            color: 'white',
                                            border: 'none',
                                            padding: '0.9rem',
                                            borderRadius: '10px',
                                            fontWeight: 'bold',
                                            fontSize: '0.95rem',
                                            cursor: 'pointer',
                                            boxShadow: '0 2px 8px rgba(239, 68, 68, 0.3)'
                                        }}
                                    >
                                        Logout
                                    </button>
                                )}
                            </div>
                        )}
                    </header>

                    <div style={{ padding: window.innerWidth > 768 ? '0 2rem 2rem' : '0 1rem 1rem' }}>
                        {/* Page Info */}
                        <div style={{
                            textAlign: 'center',
                            marginBottom: '2rem'
                        }}>
                            <h2 style={{
                                fontSize: '2rem',
                                fontWeight: 'bold',
                                color: '#1e293b',
                                marginBottom: '0.5rem'
                            }}>
                                Browse Profiles
                            </h2>
                            <p style={{
                                color: '#64748b',
                                fontSize: '1.1rem',
                                marginBottom: '0.5rem'
                            }}>
                                {fakeUserCount.toLocaleString()}+ Users Found
                            </p>
                            <p style={{
                                color: '#64748b',
                                fontSize: '1rem'
                            }}>
                                Page {currentPage + 1} of {totalPages} • Showing {currentPageProfiles.length} profiles
                            </p>
                        </div>

                        {/* Filter Section */}
                        <div style={{
                            background: 'white',
                            borderRadius: '20px',
                            boxShadow: '0 8px 25px rgba(0,0,0,0.1)',
                            padding: '1.5rem',
                            marginBottom: '2rem',
                            border: '1px solid #e2e8f0'
                        }}>
                            {/* Filter Header */}
                            <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                marginBottom: '1.5rem',
                                flexWrap: 'wrap',
                                gap: '1rem'
                            }}>
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.75rem'
                                }}>
                                    <FaFilter style={{
                                        color: '#6a11cb',
                                        fontSize: '1.2rem'
                                    }} />
                                    <h3 style={{
                                        margin: 0,
                                        fontSize: '1.4rem',
                                        fontWeight: 'bold',
                                        color: '#1e293b'
                                    }}>
                                        Filter Profiles
                                    </h3>
                                </div>

                                <div style={{
                                    display: 'flex',
                                    gap: '0.75rem',
                                    alignItems: 'center'
                                }}>
                                    <button
                                        onClick={() => setShowFilters(!showFilters)}
                                        style={{
                                            background: showFilters ?
                                                'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)' :
                                                'linear-gradient(135deg, #6a11cb 0%, #2575fc 100%)',
                                            color: 'white',
                                            border: 'none',
                                            padding: '0.75rem 1.5rem',
                                            borderRadius: '12px',
                                            fontWeight: 'bold',
                                            fontSize: '0.9rem',
                                            cursor: 'pointer',
                                            transition: 'all 0.3s ease',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '0.5rem',
                                            boxShadow: showFilters ?
                                                '0 4px 12px rgba(239, 68, 68, 0.3)' :
                                                '0 4px 12px rgba(106, 17, 203, 0.3)'
                                        }}
                                        onMouseOver={(e) => {
                                            e.target.style.transform = 'translateY(-2px)';
                                        }}
                                        onMouseOut={(e) => {
                                            e.target.style.transform = 'translateY(0)';
                                        }}
                                    >
                                        <FaFilter />
                                        {showFilters ? 'Hide Filters' : 'Show Filters'}
                                    </button>

                                    {(filters.gender || filters.religion || filters.caste || filters.district ||
                                        filters.taluka || filters.education || filters.maritalStatus ||
                                        filters.ageMin || filters.ageMax) && (
                                            <button
                                                onClick={clearFilters}
                                                style={{
                                                    background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                                                    color: 'white',
                                                    border: 'none',
                                                    padding: '0.75rem 1.5rem',
                                                    borderRadius: '12px',
                                                    fontWeight: 'bold',
                                                    fontSize: '0.9rem',
                                                    cursor: 'pointer',
                                                    transition: 'all 0.3s ease',
                                                    boxShadow: '0 4px 12px rgba(245, 158, 11, 0.3)'
                                                }}
                                                onMouseOver={(e) => {
                                                    e.target.style.transform = 'translateY(-2px)';
                                                }}
                                                onMouseOut={(e) => {
                                                    e.target.style.transform = 'translateY(0)';
                                                }}
                                            >
                                                Clear All Filters
                                            </button>
                                        )}
                                </div>
                            </div>

                            {/* Filter Options */}
                            {showFilters && (
                                <div className="filter-section filter-grid" style={{
                                    padding: '1.5rem',
                                    background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
                                    borderRadius: '15px',
                                    border: '1px solid #e2e8f0'
                                }}>
                                    {/* Gender Filter */}
                                    <div className="filter-item">
                                        <label style={{ fontSize: '0.9rem', fontWeight: '600', color: '#374151', marginBottom: '0.25rem' }}>
                                            Gender
                                        </label>
                                        <select
                                            value={filters.gender}
                                            onChange={(e) => handleFilterChange('gender', e.target.value)}
                                            className="filter-input"
                                            style={{
                                                padding: '0.75rem',
                                                borderRadius: '8px',
                                                border: '2px solid #e5e7eb',
                                                fontSize: '0.9rem',
                                                background: 'white',
                                                cursor: 'pointer',
                                                outline: 'none',
                                                width: '100%'
                                            }}
                                        >
                                            <option value="">All Genders</option>
                                            <option value="Male">Male</option>
                                            <option value="Female">Female</option>
                                        </select>
                                    </div>

                                    {/* Age Range Filter */}
                                    <div className="filter-item">
                                        <label style={{ fontSize: '0.9rem', fontWeight: '600', color: '#374151', marginBottom: '0.25rem' }}>
                                            Age Range
                                        </label>
                                        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', width: '100%' }}>
                                            <input
                                                type="number"
                                                placeholder="Min"
                                                value={filters.ageMin}
                                                onChange={(e) => handleFilterChange('ageMin', e.target.value)}
                                                className="filter-input"
                                                min="18"
                                                max="80"
                                                style={{
                                                    padding: '0.75rem',
                                                    borderRadius: '8px',
                                                    border: '2px solid #e5e7eb',
                                                    fontSize: '0.9rem',
                                                    background: 'white',
                                                    outline: 'none',
                                                    width: '45%',
                                                    minWidth: '0'
                                                }}
                                            />
                                            <span style={{ color: '#6b7280', fontWeight: '500' }}>-</span>
                                            <input
                                                type="number"
                                                placeholder="Max"
                                                value={filters.ageMax}
                                                onChange={(e) => handleFilterChange('ageMax', e.target.value)}
                                                className="filter-input"
                                                min="18"
                                                max="80"
                                                style={{
                                                    padding: '0.75rem',
                                                    borderRadius: '8px',
                                                    border: '2px solid #e5e7eb',
                                                    fontSize: '0.9rem',
                                                    background: 'white',
                                                    outline: 'none',
                                                    width: '45%',
                                                    minWidth: '0'
                                                }}
                                            />
                                        </div>
                                    </div>

                                    {/* Marital Status Filter */}
                                    <div className="filter-item">
                                        <label style={{ fontSize: '0.9rem', fontWeight: '600', color: '#374151', marginBottom: '0.25rem' }}>
                                            Marital Status
                                        </label>
                                        <select
                                            value={filters.maritalStatus}
                                            onChange={(e) => handleFilterChange('maritalStatus', e.target.value)}
                                            className="filter-input"
                                            style={{
                                                padding: '0.75rem',
                                                borderRadius: '8px',
                                                border: '2px solid #e5e7eb',
                                                fontSize: '0.9rem',
                                                background: 'white',
                                                cursor: 'pointer',
                                                outline: 'none',
                                                width: '100%'
                                            }}
                                        >
                                            <option value="">All Status</option>
                                            {(filterOptions.maritalStatuses || []).map(status => (
                                                <option key={status} value={status}>{status}</option>
                                            ))}
                                            <option value="Single">Single</option>
                                            <option value="Married">Married</option>
                                            <option value="Divorced">Divorced</option>
                                            <option value="Widowed">Widowed</option>
                                        </select>
                                    </div>

                                    {/* Religion Filter */}
                                    <div className="filter-item">
                                        <label style={{ fontSize: '0.9rem', fontWeight: '600', color: '#374151', marginBottom: '0.25rem' }}>
                                            Religion
                                        </label>
                                        <select
                                            value={filters.religion}
                                            onChange={(e) => handleFilterChange('religion', e.target.value)}
                                            className="filter-input"
                                            style={{
                                                padding: '0.75rem',
                                                borderRadius: '8px',
                                                border: '2px solid #e5e7eb',
                                                fontSize: '0.9rem',
                                                background: 'white',
                                                cursor: 'pointer',
                                                outline: 'none',
                                                width: '100%'
                                            }}
                                        >
                                            <option value="">All Religions</option>
                                            {(filterOptions.religions || []).map(religion => (
                                                <option key={religion} value={religion}>{religion}</option>
                                            ))}
                                        </select>
                                    </div>

                                    {/* Caste Filter */}
                                    <div className="filter-item">
                                        <label style={{ fontSize: '0.9rem', fontWeight: '600', color: '#374151', marginBottom: '0.25rem' }}>
                                            Caste
                                        </label>
                                        <select
                                            value={filters.caste}
                                            onChange={(e) => handleFilterChange('caste', e.target.value)}
                                            className="filter-input"
                                            style={{
                                                padding: '0.75rem',
                                                borderRadius: '8px',
                                                border: '2px solid #e5e7eb',
                                                fontSize: '0.9rem',
                                                background: 'white',
                                                cursor: 'pointer',
                                                outline: 'none',
                                                width: '100%'
                                            }}
                                        >
                                            <option value="">All Castes</option>
                                            {(filterOptions.castes || []).map(caste => (
                                                <option key={caste} value={caste}>{caste}</option>
                                            ))}
                                        </select>
                                    </div>

                                    {/* State Filter */}
                                    <div className="filter-item">
                                        <label style={{ fontSize: '0.9rem', fontWeight: '600', color: '#374151', marginBottom: '0.25rem' }}>
                                            State
                                        </label>
                                        <select
                                            value={filters.state}
                                            onChange={(e) => handleFilterChange('state', e.target.value)}
                                            className="filter-input"
                                            style={{
                                                padding: '0.75rem',
                                                borderRadius: '8px',
                                                border: '2px solid #e5e7eb',
                                                fontSize: '0.9rem',
                                                background: 'white',
                                                cursor: 'pointer',
                                                outline: 'none',
                                                width: '100%'
                                            }}
                                        >
                                            <option value="">All States</option>
                                            <option value="Maharashtra">Maharashtra</option>
                                            <option value="Bihar">Bihar</option>
                                        </select>
                                    </div>

                                    {/* District Filter */}
                                    <div className="filter-item">
                                        <label style={{ fontSize: '0.9rem', fontWeight: '600', color: '#374151', marginBottom: '0.25rem' }}>
                                            District
                                        </label>
                                        <select
                                            value={filters.district}
                                            onChange={(e) => handleFilterChange('district', e.target.value)}
                                            className="filter-input"
                                            style={{
                                                padding: '0.75rem',
                                                borderRadius: '8px',
                                                border: '2px solid #e5e7eb',
                                                fontSize: '0.9rem',
                                                background: 'white',
                                                cursor: 'pointer',
                                                outline: 'none',
                                                width: '100%'
                                            }}
                                        >
                                            <option value="">All Districts</option>
                                            {(filterOptions.districts || []).map(district => (
                                                <option key={district} value={district}>{district}</option>
                                            ))}
                                        </select>
                                    </div>

                                    {/* Taluka Filter */}
                                    <div className="filter-item">
                                        <label style={{ fontSize: '0.9rem', fontWeight: '600', color: '#374151', marginBottom: '0.25rem' }}>
                                            Taluka
                                        </label>
                                        <select
                                            value={filters.taluka}
                                            onChange={(e) => handleFilterChange('taluka', e.target.value)}
                                            className="filter-input"
                                            style={{
                                                padding: '0.75rem',
                                                borderRadius: '8px',
                                                border: '2px solid #e5e7eb',
                                                fontSize: '0.9rem',
                                                background: 'white',
                                                cursor: 'pointer',
                                                outline: 'none',
                                                width: '100%'
                                            }}
                                        >
                                            <option value="">All Talukas</option>
                                            {(filterOptions.talukas || []).map(taluka => (
                                                <option key={taluka} value={taluka}>{taluka}</option>
                                            ))}
                                        </select>
                                    </div>

                                    {/* Education Filter */}
                                    <div className="filter-item">
                                        <label style={{ fontSize: '0.9rem', fontWeight: '600', color: '#374151', marginBottom: '0.25rem' }}>
                                            Education
                                        </label>
                                        <select
                                            value={filters.education}
                                            onChange={(e) => handleFilterChange('education', e.target.value)}
                                            className="filter-input"
                                            style={{
                                                padding: '0.75rem',
                                                borderRadius: '8px',
                                                border: '2px solid #e5e7eb',
                                                fontSize: '0.9rem',
                                                background: 'white',
                                                cursor: 'pointer',
                                                outline: 'none',
                                                width: '100%'
                                            }}
                                        >
                                            <option value="">All Education</option>
                                            {(filterOptions.educations || []).map(edu => (
                                                <option key={edu} value={edu}>{edu}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                            )}

                            {/* Active Filters Display */}
                            {(filters.gender || filters.religion || filters.caste || filters.district ||
                                filters.taluka || filters.education || filters.maritalStatus ||
                                filters.ageMin || filters.ageMax) && (
                                    <div style={{
                                        marginTop: '1.5rem',
                                        padding: '1rem',
                                        background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
                                        borderRadius: '12px',
                                        border: '1px solid #bfdbfe'
                                    }}>
                                        <h4 style={{
                                            margin: '0 0 0.75rem 0',
                                            fontSize: '1rem',
                                            fontWeight: '600',
                                            color: '#1e40af'
                                        }}>
                                            Active Filters:
                                        </h4>
                                        <div style={{
                                            display: 'flex',
                                            flexWrap: 'wrap',
                                            gap: '0.5rem'
                                        }}>
                                            {filters.gender && (
                                                <span className="filter-badge" style={{
                                                    background: 'linear-gradient(135deg, #6a11cb 0%, #2575fc 100%)',
                                                    color: 'white',
                                                    padding: '0.4rem 0.8rem',
                                                    borderRadius: '20px',
                                                    fontSize: '0.8rem',
                                                    fontWeight: '500'
                                                }}>
                                                    Gender: {filters.gender}
                                                </span>
                                            )}
                                            {filters.religion && (
                                                <span style={{
                                                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                                                    color: 'white',
                                                    padding: '0.4rem 0.8rem',
                                                    borderRadius: '20px',
                                                    fontSize: '0.8rem',
                                                    fontWeight: '500'
                                                }}>
                                                    Religion: {filters.religion}
                                                </span>
                                            )}
                                            {filters.caste && (
                                                <span style={{
                                                    background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                                                    color: 'white',
                                                    padding: '0.4rem 0.8rem',
                                                    borderRadius: '20px',
                                                    fontSize: '0.8rem',
                                                    fontWeight: '500'
                                                }}>
                                                    Caste: {filters.caste}
                                                </span>
                                            )}
                                            {filters.state && (
                                                <span style={{
                                                    background: 'linear-gradient(135deg, #4f46e5 0%, #3730a3 100%)',
                                                    color: 'white',
                                                    padding: '0.4rem 0.8rem',
                                                    borderRadius: '20px',
                                                    fontSize: '0.8rem',
                                                    fontWeight: '500'
                                                }}>
                                                    State: {filters.state}
                                                </span>
                                            )}
                                            {filters.maritalStatus && (
                                                <span style={{
                                                    background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                                                    color: 'white',
                                                    padding: '0.4rem 0.8rem',
                                                    borderRadius: '20px',
                                                    fontSize: '0.8rem',
                                                    fontWeight: '500'
                                                }}>
                                                    Status: {filters.maritalStatus}
                                                </span>
                                            )}
                                            {filters.district && (
                                                <span style={{
                                                    background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                                                    color: 'white',
                                                    padding: '0.4rem 0.8rem',
                                                    borderRadius: '20px',
                                                    fontSize: '0.8rem',
                                                    fontWeight: '500'
                                                }}>
                                                    District: {filters.district}
                                                </span>
                                            )}
                                            {filters.taluka && (
                                                <span style={{
                                                    background: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)',
                                                    color: 'white',
                                                    padding: '0.4rem 0.8rem',
                                                    borderRadius: '20px',
                                                    fontSize: '0.8rem',
                                                    fontWeight: '500'
                                                }}>
                                                    Taluka: {filters.taluka}
                                                </span>
                                            )}
                                            {filters.education && (
                                                <span style={{
                                                    background: 'linear-gradient(135deg, #ec4899 0%, #db2777 100%)',
                                                    color: 'white',
                                                    padding: '0.4rem 0.8rem',
                                                    borderRadius: '20px',
                                                    fontSize: '0.8rem',
                                                    fontWeight: '500'
                                                }}>
                                                    Education: {filters.education}
                                                </span>
                                            )}
                                            {(filters.ageMin || filters.ageMax) && (
                                                <span style={{
                                                    background: 'linear-gradient(135deg, #14b8a6 0%, #0d9488 100%)',
                                                    color: 'white',
                                                    padding: '0.4rem 0.8rem',
                                                    borderRadius: '20px',
                                                    fontSize: '0.8rem',
                                                    fontWeight: '500'
                                                }}>
                                                    Age: {filters.ageMin || '18'} - {filters.ageMax || '80'}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                )}

                            {/* Filter Results Summary - only show when no filters active */}
                            {!Object.values(filters).some(v => v !== '') && (
                                <div style={{
                                    marginTop: '1.5rem',
                                    padding: '1rem',
                                    background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
                                    borderRadius: '12px',
                                    border: '1px solid #bbf7d0',
                                    textAlign: 'center'
                                }}>
                                    <p style={{
                                        margin: 0,
                                        fontSize: '1rem',
                                        fontWeight: '600',
                                        color: '#166534'
                                    }}>
                                        Browse through our extensive collection of profiles
                                    </p>
                                </div>
                            )}
                        </div>

                        {/* Loading State - Only for initial load, NOT for background loading */}
                        {loading ? (
                            <div style={{
                                background: 'white',
                                borderRadius: '20px',
                                boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
                                padding: '4rem 2rem',
                                textAlign: 'center',
                                marginBottom: '3rem'
                            }}>
                                <div style={{
                                    display: 'inline-block',
                                    width: '50px',
                                    height: '50px',
                                    border: '5px solid #f3f3f3',
                                    borderTop: '5px solid #6a11cb',
                                    borderRadius: '50%',
                                    animation: 'spin 1s linear infinite',
                                    marginBottom: '2rem'
                                }}></div>
                                <h3 style={{
                                    color: '#1e293b',
                                    marginBottom: '1rem',
                                    fontSize: '2rem',
                                    fontWeight: 'bold'
                                }}>
                                    Loading Profiles...
                                </h3>
                                <p style={{
                                    color: '#64748b',
                                    fontSize: '1.2rem'
                                }}>
                                    Please wait while we fetch the latest profiles for you
                                </p>
                                <style>
                                    {`
                                @keyframes spin {
                                    0% { transform: rotate(0deg); }
                                    100% { transform: rotate(360deg); }
                                }
                            `}
                                </style>
                            </div>
                        ) : (
                            <>
                                {/* Profiles Grid */}
                                <div style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                                    gap: '1.5rem',
                                    marginBottom: '3rem'
                                }}>
                                    {currentPageProfiles.map((profile) => (
                                        <div
                                            key={profile.id}
                                            onClick={() => handleProfileClick(profile)}
                                            style={{
                                                background: 'white',
                                                borderRadius: '20px',
                                                boxShadow: '0 8px 25px rgba(0,0,0,0.1)',
                                                overflow: 'hidden',
                                                transition: 'all 0.3s ease',
                                                cursor: 'pointer',
                                                border: '1px solid transparent'
                                            }}
                                            onMouseOver={(e) => {
                                                e.currentTarget.style.transform = 'translateY(-5px)';
                                                e.currentTarget.style.boxShadow = '0 15px 35px rgba(0,0,0,0.15)';
                                                e.currentTarget.style.border = '1px solid #6a11cb';
                                            }}
                                            onMouseOut={(e) => {
                                                e.currentTarget.style.transform = 'translateY(0)';
                                                e.currentTarget.style.boxShadow = '0 8px 25px rgba(0,0,0,0.1)';
                                                e.currentTarget.style.border = '1px solid transparent';
                                            }}
                                        >
                                            {/* Profile Image */}
                                            <div style={{
                                                height: window.innerWidth <= 768 ? '450px' : '350px',
                                                overflow: 'hidden',
                                                position: 'relative',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                backgroundColor: '#f8fafc'
                                            }}>
                                                {profile.photo ? (
                                                    <img
                                                        src={profile.photo}
                                                        alt={profile.name}
                                                        loading="lazy"
                                                        style={{
                                                            width: '100%',
                                                            height: '100%',
                                                            objectFit: 'cover',
                                                            objectPosition: 'center top'
                                                        }}
                                                    />
                                                ) : (
                                                    <div style={{
                                                        color: '#64748b',
                                                        fontSize: '4rem',
                                                        textAlign: 'center'
                                                    }}>
                                                        👤
                                                    </div>
                                                )}
                                            </div>

                                            {/* Profile Details */}
                                            <div style={{ padding: '1.5rem' }}>
                                                <h3 style={{
                                                    margin: '0 0 0.5rem 0',
                                                    fontSize: '1.4rem',
                                                    fontWeight: 'bold',
                                                    color: '#1e293b'
                                                }}>
                                                    {profile.name}
                                                </h3>

                                                <div style={{
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    gap: '0.5rem',
                                                    marginBottom: '1rem'
                                                }}>
                                                    <div style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '0.5rem',
                                                        color: '#64748b',
                                                        fontSize: '0.95rem'
                                                    }}>
                                                        <span style={{ fontWeight: '600', color: '#1e293b' }}>Age:</span>
                                                        {profile.age}
                                                    </div>

                                                    <div style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '0.5rem',
                                                        color: '#64748b',
                                                        fontSize: '0.95rem'
                                                    }}>
                                                        <span style={{ fontWeight: '600', color: '#1e293b' }}>Caste:</span>
                                                        {profile.caste}
                                                    </div>

                                                    <div style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '0.5rem',
                                                        color: '#64748b',
                                                        fontSize: '0.95rem'
                                                    }}>
                                                        <FaGraduationCap style={{ color: '#6a11cb' }} />
                                                        <span>{profile.education}</span>
                                                    </div>

                                                    <div style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '0.5rem',
                                                        color: '#64748b',
                                                        fontSize: '0.95rem'
                                                    }}>
                                                        <FaBriefcase style={{ color: '#6a11cb' }} />
                                                        <span>{profile.profession}</span>
                                                    </div>
                                                </div>

                                                <div style={{
                                                    background: 'linear-gradient(135deg, #6a11cb 0%, #2575fc 100%)',
                                                    color: 'white',
                                                    padding: '0.75rem',
                                                    borderRadius: '10px',
                                                    textAlign: 'center',
                                                    fontWeight: 'bold',
                                                    fontSize: '0.9rem'
                                                }}>
                                                    Click to View Details
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                {/* Pagination - show when multiple pages OR more data available to load */}
                                {(totalPages > 1 || (hasMoreData && !allDataLoaded)) && (
                                    <div style={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        gap: '1rem',
                                        marginBottom: '3rem',
                                        flexWrap: 'wrap'
                                    }}>
                                        {totalPages > 1 && (
                                            <button
                                                onClick={() => handlePageChange(currentPage - 1)}
                                                disabled={currentPage === 0}
                                                style={{
                                                    background: currentPage === 0 ? '#f3f4f6' : 'linear-gradient(135deg, #6a11cb 0%, #2575fc 100%)',
                                                    color: currentPage === 0 ? '#9ca3af' : 'white',
                                                    border: 'none',
                                                    padding: '0.75rem 1.5rem',
                                                    borderRadius: '10px',
                                                    cursor: currentPage === 0 ? 'not-allowed' : 'pointer',
                                                    fontWeight: 'bold',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '0.5rem'
                                                }}
                                            >
                                                <FaChevronLeft /> Previous
                                            </button>
                                        )}

                                        {totalPages > 1 && (
                                            <div style={{
                                                display: 'flex',
                                                gap: '0.5rem',
                                                flexWrap: 'wrap'
                                            }}>
                                                {Array.from({ length: Math.min(totalPages, 10) }, (_, i) => {
                                                    let pageNum;
                                                    if (totalPages <= 10) {
                                                        pageNum = i;
                                                    } else if (currentPage < 5) {
                                                        pageNum = i;
                                                    } else if (currentPage >= totalPages - 5) {
                                                        pageNum = totalPages - 10 + i;
                                                    } else {
                                                        pageNum = currentPage - 4 + i;
                                                    }

                                                    return (
                                                        <button
                                                            key={pageNum}
                                                            onClick={() => handlePageChange(pageNum)}
                                                            style={{
                                                                background: pageNum === currentPage ?
                                                                    'linear-gradient(135deg, #6a11cb 0%, #2575fc 100%)' :
                                                                    'white',
                                                                color: pageNum === currentPage ? 'white' : '#6a11cb',
                                                                border: '2px solid #6a11cb',
                                                                padding: '0.5rem 1rem',
                                                                borderRadius: '8px',
                                                                cursor: 'pointer',
                                                                fontWeight: 'bold',
                                                                minWidth: '40px'
                                                            }}
                                                        >
                                                            {pageNum + 1}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        )}

                                        {/* Next button - shows when more pages exist OR more data can be loaded */}
                                        {(currentPage < totalPages - 1 || (hasMoreData && !allDataLoaded)) && (
                                            <button
                                                onClick={() => handlePageChange(currentPage + 1)}
                                                disabled={isLoadingMore}
                                                style={{
                                                    background: isLoadingMore ? '#f3f4f6' : 'linear-gradient(135deg, #6a11cb 0%, #2575fc 100%)',
                                                    color: isLoadingMore ? '#9ca3af' : 'white',
                                                    border: 'none',
                                                    padding: '0.75rem 1.5rem',
                                                    borderRadius: '10px',
                                                    cursor: isLoadingMore ? 'wait' : 'pointer',
                                                    fontWeight: 'bold',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '0.5rem'
                                                }}
                                            >
                                                {isLoadingMore ? 'Loading...' : (currentPage >= totalPages - 1 ? 'Load More Profiles' : 'Next')} <FaChevronRight />
                                            </button>
                                        )}
                                    </div>
                                )}
                            </>
                        )}
                    </div>

                    {/* Footer */}
                    <footer style={{
                        background: '#1e293b',
                        color: 'white',
                        padding: '2rem',
                        textAlign: 'center',
                        marginTop: '3rem'
                    }}>
                        <div style={{ color: '#94a3b8', fontSize: '1rem' }}>
                            &copy; 2024 Swrajya Sangam. All rights reserved. Developed by Samyak Web Developer
                        </div>
                    </footer>
                </>
            )}

            {/* Show loading while checking registration */}
            {checkingRegistration && (
                <div style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: '100vh',
                    flexDirection: 'column',
                    gap: '1rem'
                }}>
                    <div style={{
                        width: '50px',
                        height: '50px',
                        border: '5px solid #f3f3f3',
                        borderTop: '5px solid #6a11cb',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite'
                    }}></div>
                    <p style={{ color: '#64748b', fontSize: '1.2rem' }}>Loading...</p>
                    <style>
                        {`
                            @keyframes spin {
                                0% { transform: rotate(0deg); }
                                100% { transform: rotate(360deg); }
                            }
                        `}
                    </style>
                </div>
            )}
        </div>
    );
};

export default UserHomePage;